import { useEffect } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { ReadablePalette } from '../common/Color';
import { u } from '../common/Units';

/*
 * Shared parts of the popups shown on top of a view (DM object, Minne): the dimmed background, the close
 * button, the scrollbar and the animations.
 */

/**
 * Visible scrollbar in a discreet gray (the same as behind the images), so visitors can see that there is more to scroll.
 * Always shown when the content can scroll. Needs the class "scroll", since index.css hides scrollbars on all
 * other elements
 */
export const modalScrollbar = css<{ $palette: ReadablePalette }>`
  &::-webkit-scrollbar {
    width: ${u(8)};
  }

  /* The bar follows the content: the dialog's padding, or the full column when the class "column" is set */
  &::-webkit-scrollbar-track {
    margin: ${u(40)} 0;
    background: transparent;
  }

  &.column::-webkit-scrollbar-track {
    margin: 0;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: ${u(4)};
    background-color: ${p => p.$palette.imageBackgroundColor};
  }
`;

export const modalFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const modalSlideIn = keyframes`
  from { opacity: 0; transform: translateY(${u(30)}); }
  to { opacity: 1; transform: translateY(0); }
`;

/** Dimmed background that centers the dialog. Clicking it usually closes the popup */
export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.6);
  animation: ${modalFadeIn} 0.2s;
`;

export const ModalCloseButton = styled.button<{ $palette: ReadablePalette }>`
  position: absolute;
  top: ${u(20)};
  right: ${u(20)};
  z-index: 2;
  width: ${u(60)};
  height: ${u(60)};
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${p => p.$palette.isDark ? "rgba(255, 255, 255, 0.14)" : "rgba(0, 0, 0, 0.08)"};
  color: ${p => p.$palette.textColor};
  cursor: pointer;

  & svg {
    width: ${u(22)};
    height: ${u(22)};
  }

  & svg path:first-child {
    fill: currentColor;
  }
`;

/**
 * Run a handler (usually close) when Escape is pressed. Ignored while a lightbox is open on top of the popup,
 * so Escape only closes the lightbox
 * @param {function} onEscape Handler
 * @param {boolean} enabled Whether to listen
 */
export const useEscapeKey = (onEscape: () => void, enabled: boolean = true) => {
  useEffect(() => {
    if (!enabled) { return; }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !document.body.classList.contains("lightbox-open")) { onEscape(); }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onEscape, enabled]);
}

/**
 * Set the class "modal-open" on body while a popup is open, so overlays on top of the page (e.g. the scroll
 * indicator) can hide meanwhile
 * @param {boolean} isOpen Whether the popup is shown as a dialog
 */
export const useModalOpenClass = (isOpen: boolean = true) => {
  useEffect(() => {
    if (!isOpen) { return; }
    document.body.classList.add("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, [isOpen]);
}
